import { MenuItem, Typography } from "@mui/material";
import { useState } from "react";

import VehicleDamageViewModal from "@/common/components/Entity/VehicleDamage/View/VehicleDamageViewModal";
import AppIcon from "@/common/components/Icons/AppIcon";
import MenuWithTrigger from "@/common/components/Menu/MenuWithTrigger";
import { StringHelper } from "@/common/helpers/string";
import { useApiRequest } from "@/common/hooks/api/useApiRequest";
import { VehicleDamageDtoAll, VehicleDamageDtoAny } from "@/common/mapping/mappers/vehicleDamage";
import { ApiEnumName, enumService } from "@/common/services/enum";
import { apiClient } from "@/core/api/ApiClient";
import { EntityFormType } from "@/core/api/generated/v1";

import AppIconButton from "../../Button/AppIconButton";
import BaseEntityInline, {
  BaseEntityInlineInheritableProps,
} from "../components/EntityInline/BaseEntityInline";
import { VehicleDamageDataViewModal } from "./View/VehicleDamageDataViewModal";

export interface VehicleDamageInlineProps extends BaseEntityInlineInheritableProps {
  entity: Nil<VehicleDamageDtoAny>;
  entityId?: string | null;
  withMenu?: boolean;
}

export default function VehicleDamageInline({
  entity,
  entityId,
  withMenu = false,
  ...otherProps
}: VehicleDamageInlineProps) {
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);

  const request = useApiRequest(
    apiClient.vehicleDamagesApi.apiV1VehicleDamagesVehicleDamageIdGet,
    {
      nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
      vehicleDamageId: entityId!,
    },
    {
      deps: [entityId],
      skip: !entityId || !!entity,
    },
  );
  entity = request?.data || entity;
  const entityAll = entity as Nil<VehicleDamageDtoAll>;

  const entityIdComputed = entityAll?.id || entityId;
  const vehicleIdComputed = entityAll?.vehicleId || entityAll?.vehicle?.id;

  // const title = entity2 ? getVehicleDamageOptionTitle(entity2) || null : null;
  const title = !entityAll
    ? null
    : StringHelper.join(
        [
          (entityAll.partType || entityAll.partTypeLocal) &&
          (entityAll.damageType || entityAll.damageTypeLocal)
            ? `${entityAll.partType?.name || entityAll.partTypeLocal?.name} - ${entityAll.damageType?.name || entityAll.damageTypeLocal?.name}`
            : entityAll.localNumber,
          entityAll.formType === EntityFormType.Entity
            ? enumService.getEnumValueName(ApiEnumName.VehicleDamageState, entityAll.state)
            : undefined,
          entityAll.localNumber,
        ],
        ", ",
        {
          skipEmpty: true,
          uniqueOnly: true,
        },
      );
  if (!title) {
    return null;
  }

  return (
    <>
      <BaseEntityInline
        entity={entity}
        entityId={entityId}
        request={request}
        icon={<AppIcon of='vehicleDamage' inText />}
        content={
          <>
            {title}{" "}
            {withMenu && (
              <MenuWithTrigger
                withAuthCloseOnClick
                trigger={
                  <AppIconButton size='small' sx={{ ml: 0.5, p: 0.25 }}>
                    <AppIcon of='moreVert' />
                  </AppIconButton>
                }
              >
                <MenuItem
                  dense
                  onClick={(e) => {
                    e.currentTarget.blur();
                    setIsViewModalOpen(true);
                  }}
                >
                  <AppIcon of='quickView' fontSize='small' sx={{ mr: 1 }} />
                  <Typography component='span'>Quick view</Typography>
                </MenuItem>
              </MenuWithTrigger>
            )}
          </>
        }
        {...otherProps}
        tooltipTitle={otherProps?.tooltipTitle || "Vehicle damage"}
      />

      {/* Vehicle damage view modal */}
      {entityIdComputed && (
        <VehicleDamageViewModal
          open={isViewModalOpen}
          onClose={(e) => {
            setIsViewModalOpen(false);
          }}
          viewProps={{
            vehicleId: vehicleIdComputed,
            vehicleDamageId: entityIdComputed,
            onDelete: null,
          }}
        />
      )}

      {/* Vehicle damage data view modal */}
      {!entityIdComputed && entity && (
        <VehicleDamageDataViewModal
          open={isViewModalOpen}
          onClose={() => {
            setIsViewModalOpen(false);
          }}
          viewProps={{
            entityData: entity,
          }}
        />
      )}
    </>
  );
}
